import {
  Card,
  Typography,
  Input,
  Button,
  Form,
  Divider,
  Select,
  Radio,
  DatePicker,
} from "antd";
import { LeftOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { changeLoader } from "../../../redux/reducers/loader";
import globalRequest from "../../../utils/global/globalRequest";
import * as Validation from "../../../utils/helper/validation";
import * as Apis from "../../../utils/helper/enum";
import { useDispatch } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { addDeleteGetLocalStorage, storageKeys } from "../../../utils/global/localData";
import { checkPermissionAction, formatDateFunction } from "../../../utils/helper/utils";
import toast from "react-hot-toast";
import { useEffect, useState } from "react";
import dayjs from "dayjs";

const discountTypeList = [
  { value: "flat", label: "Flat" },
  { value: "flat_cap", label: "Flat Cap" },
  { value: "percentage", label: "Percentage" },
];

const CreateCannedCoupon = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [discountFor, setDiscountFor] = useState("coupon_code");
  const [discountType, setDiscountType] = useState("flat");
  const [selectVendors, setSelectVendors] = useState([]);
  const [selectMachines, setSelectMachines] = useState([]);
  const [selectProducts, setSelectProducts] = useState([]);

  let { id } = useParams();

  useEffect(() => {
    const userAccess = addDeleteGetLocalStorage(storageKeys?.USER_TOKEN, {}, "get");
    let permissionJson = [];
    if (userAccess) {
      const decoded = jwtDecode(userAccess);
      permissionJson = decoded?.user?.rolePermission?.permissionJson;
    }
    if (id) {
      if (!checkPermissionAction("Manage Canned Coupon", permissionJson, "edit")) {
        navigate("/manage-canned-coupon");
      }
    } else {
      if (!checkPermissionAction("Manage Canned Coupon", permissionJson, "add")) {
        navigate("/manage-canned-coupon");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (name) => {
    form.setFields([
      {
        name,
        errors: [],
      },
    ]);
  };

  const handleSubmit = async (values) => {
    if (values.discountType === "percentage" && Number(values.couponAmount) > 100) {
      form.setFields([
        {
          name: "couponAmount",
          errors: ["Discount percentage cannot be greater than 100"],
        },
      ]);
      return;
    }

    const payload = {
      ...values,
      discountFor: values.discountFor || discountFor,
      couponCode: values.couponCode?.trim(),
      discountType: values.discountType || discountType,
      discountLevel: "vendor_level",
      appliedOn: values.appliedOn || "sub-total",
      couponExpiryDate: formatDateFunction(values.couponExpiryDate, "yyyy-mm-dd"),
      couponAmount: Number(values.couponAmount) || 0,
      minimumSpend: values.minimumSpend !== undefined && values.minimumSpend !== "" ? Number(values.minimumSpend) : 0,
      maximumDiscountValue: values.maximumDiscountValue !== undefined && values.maximumDiscountValue !== "" ? Number(values.maximumDiscountValue) : 0,
      usageLimitForCustomer: values.usageLimitForCustomer !== undefined && values.usageLimitForCustomer !== "" ? Number(values.usageLimitForCustomer) : 0,
      usageLimitOfCoupon: values.usageLimitOfCoupon !== undefined && values.usageLimitOfCoupon !== "" ? Number(values.usageLimitOfCoupon) : 0,
      vendor_ids: Array.isArray(values.vendor_ids) ? values.vendor_ids.map(String) : [],
      machine_ids: Array.isArray(values.machine_ids) ? values.machine_ids.map(String) : [],
      product_ids: Array.isArray(values.product_ids) ? values.product_ids.map(String) : [],
    };

    dispatch(changeLoader(true));
    try {
      let url = Apis?.MANAGE_CANNED_COUPON?.CREATE_COUPON;
      let method = "post";
      if (id) {
        url = Apis?.MANAGE_CANNED_COUPON?.EDIT_COUPON + "/" + id;
        method = "put";
      }
      let response = await globalRequest(url, method, payload, {}, true);
      let apiResponse = response?.data;
      if (response?.status === 200) {
        let msg = id ? "Canned Coupon updated successfully" : "Canned Coupon created successfully";
        toast.success(msg, {
          duration: 2000,
        });
        navigate("/manage-canned-coupon");
      } else {
        toast.error(apiResponse?.message || "Failed to save canned coupon");
      }
    } catch (e) {
      toast.error(e?.message || "Something went wrong");
    }
    dispatch(changeLoader(false));
  };

  const getCouponDetail = async (couponId) => {
    dispatch(changeLoader(true));
    try {
      let response = await globalRequest(
        Apis.MANAGE_CANNED_COUPON.GET_COUPON_DETAIL + "/" + couponId,
        "get",
        {},
        {},
        true
      );
      let apiResponse = response?.data;
      if (response?.status === 200) {
        let data = apiResponse?.data;
        const currentDiscountFor = data?.discountFor || "coupon_code";
        const currentDiscountType = data?.discountType || "flat";

        setDiscountFor(currentDiscountFor);
        setDiscountType(currentDiscountType);

        form.setFieldsValue({
          discountFor: currentDiscountFor,
          couponCode: data?.couponCode,
          discountType: currentDiscountType,
          couponAmount: data?.couponAmount,
          couponExpiryDate: data?.couponExpiryDate ? dayjs(data?.couponExpiryDate, "YYYY-MM-DD") : null,
          minimumSpend: data?.minimumSpend,
          maximumDiscountValue: data?.maximumDiscountValue,
          usageLimitForCustomer: data?.usageLimitForCustomer,
          usageLimitOfCoupon: data?.usageLimitOfCoupon,
          appliedOn: data?.appliedOn || "sub-total",
          vendor_ids: data?.vendor_ids ? data.vendor_ids.map(String) : [],
          machine_ids: data?.machine_ids ? data.machine_ids.map(String) : [],
          product_ids: data?.product_ids ? data.product_ids.map(String) : [],
        });
      } else {
        toast.error(apiResponse?.message || "Failed to fetch coupon details");
      }
    } catch (e) {
      toast.error(e?.message || "Something went wrong");
    }
    dispatch(changeLoader(false));
  };

  const disabledDate = (current) => {
    return current && current.isBefore(dayjs().startOf("day"));
  };

  // Get Canned Sellers / Vendors Data
  const getVendorData = async () => {
    let vendorData = [];

    const formatVendors = (list) => {
      if (!Array.isArray(list)) return [];
      return list.map((item) => {
        const id = String(item?.id || item?.userId || "");
        const label =
          item?.businessName ||
          item?.storeName ||
          item?.vendorDetail?.storeName ||
          item?.name ||
          (item?.user ? `${item.user.firstName || ""} ${item.user.lastName || ""}`.trim() : "") ||
          (item?.firstName ? `${item.firstName || ""} ${item.lastName || ""}`.trim() : "") ||
          `Vendor #${id}`;
        return { value: id, label };
      }).filter((v) => v.value);
    };

    // 1. Try dedicated canned coupon vendor list
    try {
      let res = await globalRequest(Apis.MANAGE_CANNED_COUPON.VENDOR_LIST, "get", {}, {}, true);
      let list = res?.data?.data?.data || res?.data?.data || res?.data || [];
      if (Array.isArray(list) && list.length > 0) {
        vendorData = formatVendors(list);
      }
    } catch (e) {
      console.warn("Could not fetch from canned coupon vendor-list, trying sellers list...", e);
    }

    // 2. If empty, try canned sellers list
    if (vendorData.length === 0) {
      try {
        let res = await globalRequest("api/canned-seller/list", "get", {}, { params: { dropdown: true } }, true);
        let list = res?.data?.data?.data || res?.data?.data || res?.data || [];
        if (Array.isArray(list) && list.length > 0) {
          vendorData = formatVendors(list);
        }
      } catch (e) {
        console.warn("Could not fetch from canned-seller/list, trying general vendor-list...", e);
      }
    }

    // 3. Fallback to general coupon vendor list
    if (vendorData.length === 0) {
      try {
        let res = await globalRequest(Apis.MANAGE_COUPON.VENDOR_LIST, "get", {}, {}, true);
        let list = res?.data?.data?.data || res?.data?.data || res?.data || [];
        if (Array.isArray(list) && list.length > 0) {
          vendorData = formatVendors(
            list.filter((item) => item?.vendorDetail?.vendor_type !== "host")
          );
        }
      } catch (e) {
        console.error("All vendor fetching attempts failed:", e);
      }
    }

    setSelectVendors(vendorData);
  };

  // Get Products Data
  const getProductData = async () => {
    let productOptions = [];

    const formatProducts = (list) => {
      if (!Array.isArray(list)) return [];
      return list.map((item) => {
        const id = String(item?.id || item?.Code || item?.GoodsId || item?.productId || "");
        const name =
          item?.name ||
          item?.GoodsName ||
          item?.goodsName ||
          item?.productName ||
          item?.title ||
          `Product #${id}`;
        const price = item?.Price || item?.unitPrice || item?.price;
        const label = price ? `${name} (SAR ${price})` : name;
        return { value: id, label };
      }).filter((p) => p.value);
    };

    // 1. Try dedicated canned coupon product list
    try {
      let res = await globalRequest(Apis.MANAGE_CANNED_COUPON.PRODUCT_LIST, "get", {}, {}, true);
      let list = res?.data?.data?.data || res?.data?.data || res?.data || [];
      if (Array.isArray(list) && list.length > 0) {
        productOptions = formatProducts(list);
      }
    } catch (e) {
      console.warn("Could not fetch from canned coupon product-list, trying portal products...", e);
    }

    // 2. Try canned portal products
    if (productOptions.length === 0) {
      try {
        let res = await globalRequest("api/canned/canned-products", "get", {}, {}, true);
        let list = res?.data?.data?.Data || res?.data?.data?.data || res?.data?.data || res?.data || [];
        if (Array.isArray(list) && list.length > 0) {
          productOptions = formatProducts(list);
        }
      } catch (e) {
        console.warn("Could not fetch from api/canned/canned-products, trying general products...", e);
      }
    }

    // 3. Fallback to general product list
    if (productOptions.length === 0) {
      try {
        let res = await globalRequest(Apis.MANAGE_PRODUCT.GET_ALL_PRODUCT, "get", {}, { params: { limit: 1000 } }, true);
        let list = res?.data?.data?.rows || res?.data?.data?.data || res?.data?.data || [];
        if (Array.isArray(list) && list.length > 0) {
          productOptions = formatProducts(list);
        }
      } catch (e) {
        console.error("All product fetching attempts failed:", e);
      }
    }

    setSelectProducts(productOptions);
  };

  // Get Canned Vending Machines Data
  const getMachineData = async () => {
    try {
      let response = await globalRequest(
        Apis.MANAGE_CANNED_COUPON.MACHINE_LIST,
        "get",
        {},
        { params: { limit: 1000 } },
        true
      );
      if (response?.status === 200) {
        let machines = response?.data?.data?.rows || response?.data?.data || [];
        let machineOptions = machines
          .filter((item) => item?.status !== "deleted")
          .map((item) => ({
            value: String(item?.id || item?.clientid || item?.csn),
            label: item?.name
              ? `${item.name} (${item.csn || item.clientid || item.id})`
              : `${item.csn || item.clientid || `Machine #${item.id}`}`,
          }));
        setSelectMachines(machineOptions);
      }
    } catch (e) {
      console.error("Error fetching machines:", e);
    }
  };

  useEffect(() => {
    if (id) {
      getCouponDetail(id);
    }
    getVendorData();
    getProductData();
    getMachineData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <>
      <Card>
        <div className="flex items-center justify-between relative mb-4">
          <Typography className="text-xl font-semibold mb-0" align={"left"}>
            <LeftOutlined
              onClick={() => navigate(-1)}
              className="text-lg text-black mr-2 cursor-pointer"
            />
            {id ? "Edit" : "Create"} Canned Coupon
          </Typography>
        </div>
        <Divider />
        <div className="mb-0 flex justify-between flex-wrap">
          <Form
            layout="vertical"
            style={{
              maxWidth: 1000,
              width: "100%",
            }}
            initialValues={{
              discountFor: "coupon_code",
              discountType: "flat",
              appliedOn: "sub-total",
            }}
            autoComplete="off"
            form={form}
            onFinish={handleSubmit}
          >
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                {/* Discount For */}
                <Form.Item
                  label="Discount For"
                  name="discountFor"
                  rules={[
                    {
                      required: true,
                      message: "Please select Discount For.",
                    },
                  ]}
                >
                  <Radio.Group
                    onChange={(e) => {
                      const val = e.target.value;
                      setDiscountFor(val);
                      form.setFieldsValue({ discountFor: val });
                      handleInputChange("couponCode");
                    }}
                  >
                    <Radio value="coupon_code">Coupon Code</Radio>
                    <Radio value="mobile_number">Mobile Number</Radio>
                  </Radio.Group>
                </Form.Item>

                {/* Coupon Code / Mobile Number */}
                <Form.Item
                  label={discountFor === "mobile_number" ? "Mobile Number" : "Coupon Code"}
                  name="couponCode"
                  rules={[
                    {
                      required: true,
                      message: `Please enter ${
                        discountFor === "mobile_number" ? "Mobile Number" : "Coupon Code"
                      }.`,
                    },
                    discountFor === "coupon_code"
                      ? {
                          max: 25,
                          message: "Maximum 25 characters",
                        }
                      : {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const clean = value.replace(/[\s-]/g, "");
                            if (!/^\+?\d{7,15}$/.test(clean)) {
                              return Promise.reject(
                                new Error("Please enter a valid mobile number (7-15 digits)")
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder={
                      discountFor === "mobile_number"
                        ? "e.g. 5xxxxxxxx or 966xxxxxxxxx"
                        : "Enter coupon code"
                    }
                    maxLength={discountFor === "mobile_number" ? 16 : 25}
                    onChange={(e) => {
                      let val = e.target.value;
                      if (discountFor === "mobile_number") {
                        val = val.replace(/[^0-9+]/g, "");
                      } else {
                        val = val.trimStart();
                      }
                      form.setFieldValue("couponCode", val);
                      handleInputChange("couponCode");
                    }}
                  />
                </Form.Item>

                {/* Discount Type */}
                <Form.Item
                  label="Discount Type"
                  name="discountType"
                  rules={[
                    {
                      required: true,
                      message: "Please Select Discount Type.",
                    },
                  ]}
                >
                  <Select
                    className="w-full"
                    size="large"
                    placeholder="Select Discount Type"
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    options={discountTypeList}
                    onChange={(val) => {
                      setDiscountType(val);
                      form.setFieldValue("discountType", val);
                    }}
                  />
                </Form.Item>

                {/* Discount Amount */}
                <Form.Item
                  label={
                    discountType === "flat" || discountType === "flat_cap"
                      ? "Discount Amount"
                      : "Discount Percentage"
                  }
                  name="couponAmount"
                  rules={[
                    {
                      required: true,
                      message: `Please enter ${
                        discountType === "percentage" ? "Discount Percentage" : "Discount Amount"
                      }.`,
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={10}
                    addonAfter={discountType === "flat" || discountType === "flat_cap" ? "SAR" : "%"}
                    onChange={(e) => {
                      let value = Validation.strFormat(e.target.value, "number");
                      form.setFieldValue("couponAmount", value.trimStart());
                      handleInputChange("couponAmount");
                    }}
                  />
                </Form.Item>

                {/* Expiry Date */}
                <Form.Item
                  label="Coupon Expiry Date"
                  name="couponExpiryDate"
                  rules={[
                    {
                      required: true,
                      message: "Please select Coupon Expiry Date.",
                    },
                  ]}
                >
                  <DatePicker
                    format="DD-MM-YYYY"
                    className="h-[39px] w-full"
                    disabledDate={disabledDate}
                  />
                </Form.Item>

                {/* Minimum Spend */}
                <Form.Item
                  label="Minimum Spend"
                  name="minimumSpend"
                  rules={[
                    {
                      required: true,
                      message: "Please enter Minimum Spend.",
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={10}
                    addonAfter="SAR"
                    onChange={(e) => {
                      let value = Validation.strFormat(e.target.value, "number");
                      form.setFieldValue("minimumSpend", value.trimStart());
                      handleInputChange("minimumSpend");
                    }}
                  />
                </Form.Item>

                {/* Maximum Discount Value */}
                <Form.Item
                  label="Maximum Discount Value"
                  name="maximumDiscountValue"
                  rules={[
                    {
                      required: true,
                      message: "Please enter Maximum Discount Value.",
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={10}
                    addonAfter="SAR"
                    onChange={(e) => {
                      let value = Validation.strFormat(e.target.value, "number");
                      form.setFieldValue("maximumDiscountValue", value.trimStart());
                      handleInputChange("maximumDiscountValue");
                    }}
                  />
                </Form.Item>

                {/* Usage limits for customers */}
                <Form.Item
                  label="Usage limits for customers"
                  name="usageLimitForCustomer"
                  rules={[
                    {
                      required: true,
                      message: "Please enter Usage limits for customers.",
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={10}
                    onChange={(e) => {
                      let value = Validation.strFormat(e.target.value, "number");
                      form.setFieldValue("usageLimitForCustomer", value.trimStart());
                      handleInputChange("usageLimitForCustomer");
                    }}
                  />
                </Form.Item>

                {/* No of coupon to be used */}
                <Form.Item
                  label="No of coupon to be used"
                  name="usageLimitOfCoupon"
                  rules={[
                    {
                      required: true,
                      message: "Please enter No of coupon to be used.",
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={10}
                    onChange={(e) => {
                      let value = Validation.strFormat(e.target.value, "number");
                      form.setFieldValue("usageLimitOfCoupon", value.trimStart());
                      handleInputChange("usageLimitOfCoupon");
                    }}
                  />
                </Form.Item>

                {/* Vendors / Sellers (Optional) */}
                <Form.Item
                  label="Vendors / Sellers"
                  name="vendor_ids"
                >
                  <Select
                    className="w-full"
                    size="large"
                    placeholder="Select Vendors / Sellers (Optional)"
                    mode="multiple"
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    options={selectVendors}
                  />
                </Form.Item>

                {/* Products (Optional) */}
                <Form.Item
                  label="Products"
                  name="product_ids"
                >
                  <Select
                    className="w-full"
                    size="large"
                    placeholder="Select Products (Optional)"
                    mode="multiple"
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    options={selectProducts}
                  />
                </Form.Item>

                {/* Canned Vending Machines */}
                <Form.Item
                  label="Canned Vending Machines"
                  name="machine_ids"
                  rules={[
                    {
                      required: true,
                      message: "Please select at least one Machine.",
                    },
                  ]}
                >
                  <Select
                    className="w-full"
                    size="large"
                    placeholder="Select Canned Vending Machines"
                    mode="multiple"
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    options={selectMachines}
                  />
                </Form.Item>

                {/* Applied On */}
                <Form.Item
                  label="To be Applied on"
                  name="appliedOn"
                  rules={[
                    {
                      required: true,
                      message: "Please select To be Applied on.",
                    },
                  ]}
                >
                  <Radio.Group>
                    <Radio value="sub-total">Sub Total (including VAT)</Radio>
                  </Radio.Group>
                </Form.Item>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button
                className="w-[100px] h-[39px]"
                onClick={() => navigate("/manage-canned-coupon")}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                className="min-w-[100px] h-[39px]"
                htmlType="submit"
              >
                {id ? "Edit" : "Create"} Coupon
              </Button>
            </div>
          </Form>
        </div>
      </Card>
    </>
  );
};

export default CreateCannedCoupon;
